<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useGlobalStore } from "@stores/global";
import { useGlobeStore } from "@stores/globe";
import { SWEEPSTAKES_END_AT } from "@utils/sweepstakes";
import PanelTabs from "@components/explore/PanelTabs.vue";
import PanelFooter from "@components/explore/PanelFooter.vue";

const globalStore = useGlobalStore();
const globeStore = useGlobeStore();

const now = ref(Date.now());
let timer;
onMounted(() => {
    timer = setInterval(() => (now.value = Date.now()), 1000);
});
onUnmounted(() => clearInterval(timer));

const units = computed(() => {
    const diff = Math.max(0, SWEEPSTAKES_END_AT - now.value);
    const s = Math.floor(diff / 1000);
    return [
        { label: "Days", value: Math.floor(s / 86400) },
        { label: "Hrs", value: Math.floor((s % 86400) / 3600) },
        { label: "Min", value: Math.floor((s % 3600) / 60) },
        { label: "Sec", value: s % 60 },
    ];
});

const digits = (n) =>
    String(Math.min(99, n)).padStart(2, "0").split("").map(Number);
</script>

<template>
    <Transition name="slide-left" appear>
        <div
            v-if="
                globalStore.panelActive('countdown') &&
                !globeStore.selectedCountry &&
                globalStore.activePage === 'explore'
            "
            class="countdown bg-marine-800 border-marine-500 flex flex-col justify-center gap-6 border-r border-b p-6 max-lg:border-0"
            :class="{
                'max-lg:hidden': globalStore.mobilePanel !== 'countdown',
            }"
        >
            <PanelTabs />

            <div
                class="flex flex-col gap-5 max-lg:h-[180px] max-lg:overflow-y-auto"
            >
                <div class="flex flex-col gap-2">
                    <p class="h6 leading-[0.9] font-bold uppercase">
                        Want a chance to meet Speed?
                    </p>
                    <p class="body-sm faded-text leading-tight">
                        Sweepstakes ends soon!
                    </p>
                </div>

                <div class="flex w-full items-start gap-2 text-center">
                    <div
                        v-for="unit in units"
                        :key="unit.label"
                        class="flex flex-1 flex-col items-center gap-3"
                    >
                        <div
                            class="bg-marine-600 relative flex w-full items-center justify-center rounded-lg px-4 py-5"
                        >
                            <div
                                v-for="(d, i) in digits(unit.value)"
                                :key="i"
                                class="firefox:pt-[0.15em] inline-block h-[0.9em] overflow-hidden text-[32px] leading-[0.9] font-extrabold text-yellow-400 uppercase"
                            >
                                <div
                                    class="flex flex-col transition-transform duration-600 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
                                    :style="{
                                        transform: `translateY(-${d * 10}%)`,
                                    }"
                                >
                                    <div
                                        class="flex h-[0.9em] shrink-0 items-center justify-center"
                                        v-for="n in 10"
                                        :key="n"
                                    >
                                        {{ n - 1 }}
                                    </div>
                                </div>
                            </div>
                            <span
                                class="bg-marine-800 absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2"
                            />
                            <span
                                class="bg-marine-400 absolute top-1/2 left-[3px] h-4 w-[3px] -translate-y-1/2 rounded-full"
                            />
                            <span
                                class="bg-marine-400 absolute top-1/2 right-[3px] h-4 w-[3px] -translate-y-1/2 rounded-full"
                            />
                        </div>
                        <span class="body-md faded-text font-medium uppercase">
                            {{ unit.label }}
                        </span>
                    </div>
                </div>
            </div>

            <PanelFooter class="lg:hidden" />
        </div>
    </Transition>
</template>

<style scoped>
.countdown {
    grid-column: 1 / 7;
    grid-row: 2 / span 3;
    overflow-y: auto;
    z-index: 2;
}

@media (max-width: 1024px) {
    .countdown {
        grid-column: 1 / -1;
        grid-row: -2 / -1;
        align-self: end;
        overflow-y: visible;
    }
}

@media (min-width: 1921px) {
    .countdown {
        grid-row: 2 / span 4;
    }
}
</style>
